import { useLazyQuery } from '@apollo/client';
import { Button, Checkbox, Divider, Empty, Tag } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { LIMIT } from '../../../../../../common/constants';
import LoaderComponent from '../../../../../../components/LoaderComponent';
import SearchComponent from '../../../../../../components/SearchComponent';
import { LIBRARIES } from '../../../../../requests/modules/library/graphql/Queries';

const LIBRARY_SORT = { field: 'createdAt', order: 'DESC' };

const LibraryFilterDropdown = ({
  open,
  appliedTags = [],
  onApply,
  onReset,
}) => {
  const [options, setOptions] = useState([]);
  const [tags, setTags] = useState(appliedTags);
  const [search, setSearch] = useState('');
  const [isEnd, setIsEnd] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);

  const [fetchLibraries] = useLazyQuery(LIBRARIES, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  const loadLibraries = async ({ skip = 0, searchTerm = search } = {}) => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setIsLoading(true);
    try {
      const response = await fetchLibraries({
        variables: {
          filter: { skip, limit: LIMIT, search: searchTerm },
          sort: LIBRARY_SORT,
        },
      });
      if (requestId !== requestIdRef.current) return;
      const pageOptions =
        response?.data?.libraries?.data?.map((library) => ({
          key: library?.id,
          label: library?.name,
        })) || [];
      setIsEnd(pageOptions?.length < LIMIT);
      setOptions((prev) => (skip ? [...prev, ...pageOptions] : pageOptions));
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setTags(appliedTags);
    setSearch('');
    setOptions([]);
    setIsEnd(false);
    loadLibraries({ skip: 0, searchTerm: '' });
  }, [open]);

  const selectedKeys = tags?.map((tag) => tag?.key);

  const visibleTags = tags
    ?.map((tag) => ({
      ...tag,
      label:
        tag?.label ||
        options?.find((option) => option?.key === tag?.key)?.label,
    }))
    ?.filter((tag) => tag?.label);

  const toggleOption = (option) =>
    setTags((prev) =>
      prev?.some((tag) => tag?.key === option?.key)
        ? prev?.filter((tag) => tag?.key !== option?.key)
        : [...prev, option],
    );

  const handleScroll = (event) => {
    const { scrollTop, offsetHeight, scrollHeight } = event?.target || {};
    if (!isLoading && !isEnd && scrollTop + offsetHeight >= scrollHeight - 5) {
      loadLibraries({ skip: options?.length });
    }
  };

  return (
    <div className="custom-filter-dropdown">
      <LoaderComponent spinning={isLoading} setHeight={35} size="medium">
        <SearchComponent
          className="filter-search"
          placeholder="Search..."
          query={search}
          setQuery={setSearch}
          getData={(value) => loadLibraries({ skip: 0, searchTerm: value })}
        />
        {visibleTags?.length > 0 && (
          <div className="filter-tags-wrapper">
            {visibleTags?.map((tag) => (
              <Tag
                key={tag?.key}
                closable
                onClose={() => toggleOption(tag)}
                className="filter-tag"
              >
                <span>{tag?.label}</span>
              </Tag>
            ))}
          </div>
        )}
        <div className="filter-checkboxes" onScroll={handleScroll}>
          {options?.length > 0
            ? options?.map((option) => (
                <div className="single-checkbox-div" key={option?.key}>
                  <Checkbox
                    checked={selectedKeys?.includes(option?.key)}
                    onChange={() => toggleOption(option)}
                    className="single-checkbox"
                  >
                    <span>{option?.label}</span>
                  </Checkbox>
                </div>
              ))
            : !isLoading && <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />}
        </div>
      </LoaderComponent>
      <Divider className="divider-filter" />
      <div className="d-flex justify-center">
        <Button size="small" className="reset-button" onClick={onReset}>
          Reset
        </Button>
        <Button
          size="small"
          className="common-button ok-button"
          type="primary"
          onClick={() => onApply(tags)}
        >
          Ok
        </Button>
      </div>
    </div>
  );
};

export default LibraryFilterDropdown;
